import React, { useMemo, useRef, useState, useEffect } from "react";
import {
    Grid, GridItem, VStack, FormControl, FormLabel, Input,
    Select as CSelect, Text, Checkbox, HStack, Button, Tooltip,
    Flex, Heading, Divider, Textarea, useToast, Kbd, Badge, useColorMode
} from "@chakra-ui/react";
import { useAssistant } from "../../context/AssistantContext";
import axios from '../../utils/axiosConfig';

const enumOptions = {
    theme: [
        { label: "Light", value: "light" },
        { label: "Dark", value: "dark" },
    ],
    position: [
        { label: "Bottom Right", value: "bottom-right" },
        { label: "Bottom Left", value: "bottom-left" },
        { label: "Top Right", value: "top-right" },
        { label: "Top Left", value: "top-left" },
    ],
    borderRadius: [
        { label: "None", value: "none" },
        { label: "Small", value: "small" },
        { label: "Medium", value: "medium" },
        { label: "Large", value: "large" },
        { label: "Full (circle/pill)", value: "full" },
    ],
};

function attrLine(name, value) {
    if (value === undefined || value === null || value === "") return "";
    const safe = typeof value === "string" ? value.replace(/"/g, "&quot;") : String(value);
    return `  ${name}="${safe}"\n`;
}

const EmbedTab = () => {
    const {
        formState,
        handleInputChange,
        handleCheckboxChange
    } = useAssistant();
    const toast = useToast();
    const [copied, setCopied] = useState(false);
    const [userPublicKey, setUserPublicKey] = useState("");
    const codeRef = useRef(null);
    const { colorMode } = useColorMode();

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get('/api/protected/profile');
                if (res.data?.data?.publicKey) {
                    setUserPublicKey(res.data.data.publicKey);
                }
            } catch (err) {
                console.error("Failed to fetch public key", err);
            }
        };
        fetchProfile();
    }, []);

    // Pull only the voice-button fields we need; provide safe defaults.
    const {
        // publicKey is now from local state
        assistantId = formState._id,
        theme = "light",
        position = "bottom-right",
        borderRadius = "full",
        // button visuals
        ctaButtonColor = theme === "dark" ? "#111111" : "#000000",
        ctaButtonTextColor = "#ffffff",
        accentColor = "#14B8A6", // used for subtle accents if your widget supports it
        // labels
        startButtonText = "Talk",
        endButtonText = "End",
        ariaLabel = "Talk to AI",
        // behavior
        voiceShowTranscript = false,
        consentRequired = false,
        consentTitle = "Terms and conditions",
        consentContent = "",
        consentStorageKey = "talk_widget_consent",
        // script
        scriptSrc = `http://localhost:3001/widget/talk-widget.v1.0.0.umd.js`,
    } = formState || {};

    const widgetTag = useMemo(() => {
        let tag = `<talk-widget\n`;
        tag += attrLine("public-key", userPublicKey);
        tag += attrLine("assistant-id", assistantId);
        tag += attrLine("mode", "voice"); // explicit voice mode
        tag += attrLine("theme", theme);
        tag += attrLine("position", position);
        tag += attrLine("border-radius", borderRadius);
        tag += attrLine("cta-button-color", ctaButtonColor);
        tag += attrLine("cta-button-text-color", ctaButtonTextColor);
        tag += attrLine("accent-color", accentColor);
        tag += attrLine("start-button-text", startButtonText);
        tag += attrLine("end-button-text", endButtonText);
        tag += attrLine("aria-label", ariaLabel);
        if (voiceShowTranscript) tag += attrLine("voice-show-transcript", "true");
        if (consentRequired) {
            tag += attrLine("consent-required", "true");
            tag += attrLine("consent-title", consentTitle);
            tag += attrLine("consent-content", consentContent);
            tag += attrLine("consent-storage-key", consentStorageKey);
        }
        tag += `></talk-widget>`;
        return tag;
    }, [
        userPublicKey,
        assistantId,
        theme,
        position,
        borderRadius,
        ctaButtonColor,
        ctaButtonTextColor,
        accentColor,
        startButtonText,
        endButtonText,
        ariaLabel,
        voiceShowTranscript,
        consentRequired,
        consentTitle,
        consentContent,
        consentStorageKey,
    ]);

    const fullEmbedCode = useMemo(() => {
        return `${widgetTag}\n\n<script src="${scriptSrc}" async></script>`;
    }, [widgetTag, scriptSrc]);

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(fullEmbedCode);
            setCopied(true);
            toast({ title: "Embed code copied!", status: "success", duration: 1800, isClosable: true });
            setTimeout(() => setCopied(false), 1200);
        } catch {
            if (codeRef.current) codeRef.current.select();
            toast({
                title: "Press",
                description: <><Kbd>Ctrl</Kbd>/<Kbd>⌘</Kbd> + <Kbd>C</Kbd> to copy</>,
                status: "info",
                duration: 2500,
                isClosable: true,
            });
        }
    };

    return (
        <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap={8}>
            {/* LEFT: Voice button config */}
            <GridItem>
                <VStack align="stretch" spacing={5}>
                    <Heading size="md">Voice Button Embed</Heading>
                    <Text color="gray.500">
                        This generates a single <Badge colorScheme="purple">Talk</Badge> button (click to start/end the call).
                        Your <Badge colorScheme="purple">public key</Badge> identifies your account but isn’t secret.
                    </Text>

                    <Divider />

                    <FormControl isRequired>
                        <FormLabel>Public Key</FormLabel>
                        <Input
                            name="publicKey"
                            placeholder="Loading..."
                            value={userPublicKey}
                            isReadOnly={true}
                            bg={colorMode === "light" ? "gray.100" : "gray.600"}
                        />
                    </FormControl>

                    <FormControl isRequired>
                        <FormLabel>Assistant ID</FormLabel>
                        <Input
                            name="assistantId"
                            placeholder="asst_abc123"
                            value={assistantId}
                            onChange={handleInputChange}
                            disabled={true}
                        />
                    </FormControl>

                    <HStack spacing={4}>
                        <FormControl>
                            <FormLabel>Theme</FormLabel>
                            <CSelect name="theme" value={theme} onChange={handleInputChange}>
                                {enumOptions.theme.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </CSelect>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Position</FormLabel>
                            <CSelect name="position" value={position} onChange={handleInputChange}>
                                {enumOptions.position.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </CSelect>
                        </FormControl>
                    </HStack>

                    <HStack spacing={4}>
                        <FormControl>
                            <FormLabel>Border Radius</FormLabel>
                            <CSelect
                                name="borderRadius"
                                value={borderRadius}
                                onChange={handleInputChange}
                            >
                                {enumOptions.borderRadius.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </CSelect>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Aria Label</FormLabel>
                            <Input
                                name="ariaLabel"
                                value={ariaLabel}
                                onChange={handleInputChange}
                                placeholder="Talk to AI"
                            />
                        </FormControl>
                    </HStack>

                    {/* Button colors */}
                    <HStack spacing={4}>
                        <FormControl>
                            <FormLabel>Button Color</FormLabel>
                            <HStack>
                                <Input
                                    name="ctaButtonColor"
                                    value={ctaButtonColor}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    type="color"
                                    aria-label="Pick button color"
                                    value={ctaButtonColor}
                                    onChange={(e) =>
                                        handleInputChange({ target: { name: "ctaButtonColor", value: e.target.value } })
                                    }
                                    w="48px"
                                    p={0}
                                />
                            </HStack>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Button Text Color</FormLabel>
                            <HStack>
                                <Input
                                    name="ctaButtonTextColor"
                                    value={ctaButtonTextColor}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    type="color"
                                    aria-label="Pick text color"
                                    value={ctaButtonTextColor}
                                    onChange={(e) =>
                                        handleInputChange({ target: { name: "ctaButtonTextColor", value: e.target.value } })
                                    }
                                    w="48px"
                                    p={0}
                                />
                            </HStack>
                        </FormControl>
                    </HStack>

                    {/* Labels */}
                    <HStack spacing={4}>
                        <FormControl>
                            <FormLabel>Start Button Text</FormLabel>
                            <Input
                                name="startButtonText"
                                value={startButtonText}
                                onChange={handleInputChange}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>End Button Text</FormLabel>
                            <Input
                                name="endButtonText"
                                value={endButtonText}
                                onChange={handleInputChange}
                            />
                        </FormControl>
                    </HStack>

                    {/* Optional toggles */}
                    <HStack spacing={6}>
                        <Checkbox
                            name="voiceShowTranscript"
                            isChecked={!!voiceShowTranscript}
                            onChange={(e) =>
                                handleCheckboxChange({
                                    target: { name: "voiceShowTranscript", checked: e.target.checked },
                                })
                            }
                        >
                            Show transcript
                        </Checkbox>
                        <Checkbox
                            name="consentRequired"
                            isChecked={!!consentRequired}
                            onChange={(e) =>
                                handleCheckboxChange({
                                    target: { name: "consentRequired", checked: e.target.checked },
                                })
                            }
                        >
                            Require consent
                        </Checkbox>
                    </HStack>

                    {consentRequired && (
                        <>
                            <FormControl>
                                <FormLabel>Consent Title</FormLabel>
                                <Input
                                    name="consentTitle"
                                    value={consentTitle}
                                    onChange={handleInputChange}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Consent Content</FormLabel>
                                <Textarea
                                    name="consentContent"
                                    value={consentContent}
                                    onChange={handleInputChange}
                                    placeholder='By clicking "Agree", you consent to recording/storage/sharing as per our ToS.'
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Consent Storage Key</FormLabel>
                                <Input
                                    name="consentStorageKey"
                                    value={consentStorageKey}
                                    onChange={handleInputChange}
                                />
                            </FormControl>
                        </>
                    )}

                </VStack>
            </GridItem>

            {/* RIGHT: Code + copy */}
            <GridItem>
                <VStack align="stretch" spacing={4}>
                    <Heading size="md">Embed code</Heading>
                    <Text color="gray.500">
                        Paste this into any website.
                    </Text>

                    <Textarea
                        ref={codeRef}
                        value={fullEmbedCode}
                        readOnly
                        minH="320px"
                        fontFamily="mono"
                        fontSize="sm"
                        bg={colorMode === "dark" ? "gray.800" : "gray.50"}
                    />

                    <Flex gap={3}>
                        <Tooltip label={copied ? "Copied!" : "Copy to clipboard"} openDelay={300}>
                            <Button onClick={copyCode} colorScheme="blue">
                                {copied ? "Copied" : "Copy"}
                            </Button>
                        </Tooltip>
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (!userPublicKey || !assistantId) {
                                    toast({
                                        title: "Missing fields",
                                        description: "Public Key and Assistant ID are required.",
                                        status: "warning",
                                        duration: 2200,
                                        isClosable: true,
                                    });
                                } else {
                                    toast({
                                        title: "Looks good!",
                                        description: "Your voice button embed is ready.",
                                        status: "success",
                                        duration: 1600,
                                        isClosable: true,
                                    });
                                }
                            }}
                        >
                            Validate
                        </Button>
                    </Flex>

                    <Divider />

                </VStack>
            </GridItem>
        </Grid>
    );
};

export default EmbedTab;